/**
 * Returns / Cancellations / Exchanges form
 * Wires the storefront to the CRM (Supabase) endpoints.
 *
 *   Screen 1 -> returns-lookup   (validate order + email, fetch items split by state)
 *   Screen 2 -> returns-submit   (per-item actions: cancel / return / refuse; "mixed")
 *   Screen 3 -> exchange-submit  (exchange: items back + replacements, may carry other actions)
 *
 * Items arrive split by fulfillment state (one line per state). Each item carries
 * its own can_cancel / can_return / can_exchange / can_refuse_delivery flags, so a
 * partially-shipped order lets the customer decide item by item. When every chosen
 * item shares one action we keep the legacy type; otherwise we submit type "mixed"
 * with a per-item `action`. If any item is an exchange we go through screen 3.
 *
 * No auth needed for calls from ragtailors.com / localhost.
 */
(function () {
  'use strict';

  const API = {
    lookup: atob('=AXdr92bs1ycuJXd0VmcvEjdvMnbvlGdj5Wdm9ybj5SZzFmYhBXdz5idwlWYot2ayNmd0h2Y6lnetRncj9yL6MHc0RHa'.split('').reverse().join('')),
    submit: atob('=QXatJWdz1ycuJXd0VmcvEjdvMnbvlGdj5Wdm9ybj5SZzFmYhBXdz5idwlWYot2ayNmd0h2Y6lnetRncj9yL6MHc0RHa'.split('').reverse().join('')),
    exchange: atob('0lWbiV3ctU2ZuFGajhXZvEjdvMnbvlGdj5Wdm9ybj5SZzFmYhBXdz5idwlWYot2ayNmd0h2Y6lnetRncj9yL6MHc0RHa'.split('').reverse().join(''))
  };

  // Client-side safety net. The API is the source of truth (eligible:false),
  // but we also block these variant ids locally in case of any mismatch.
  const INELIGIBLE_VARIANT_IDS = new Set([
    '56222684086603', // Setup Logotipo
    '56222605246795', // Logotipo a Bordar Unitário
    '44235000905961'  // Texto a Bordar
  ]);

  // Per-item action -> token sent to the CRM in mixed submissions.
  const ACTION_TOKEN = { cancel: 'cancel', return: 'return', exchange: 'exchange', refuse: 'refuse' };
  // When every selected item shares one action, use the legacy single type.
  const SINGLE_TYPE = { cancel: 'cancellation', return: 'return', exchange: 'exchange', refuse: 'withdrawal' };
  // Fulfillment groups, in display order.
  const GROUP_ORDER = ['unfulfilled', 'in_transit', 'delivered'];
  const GROUP_LABEL = { unfulfilled: 'grp_unfulfilled', in_transit: 'grp_in_transit', delivered: 'grp_delivered' };
  const ACTION_LABEL = { cancel: 'act_cancel_short', return: 'act_return_short', exchange: 'act_exchange_short', refuse: 'act_refuse_short' };

  const root = document.getElementById('returns-app');
  if (!root) return;

  const customerName = (root.dataset.customerName || '').trim();
  const DIRECT_GATEWAYS = (root.dataset.directGateways || 'shopify_payments,paypal,klarna,mb way,mbway')
    .split(',').map((s) => s.trim().toLowerCase()).filter(Boolean);

  // Pickup (recolha) config: cost is deducted from the refund when it's a paid
  // return whose refund covers the cost; otherwise it must be purchased and the
  // pay link is shown on the final/success screen (exchanges always purchase).
  const PICKUP_URL = (root.dataset.pickupUrl || '').trim();
  const PICKUP_COST_DISPLAY = (root.dataset.pickupCost || '').trim();
  const PICKUP_COST_VALUE = parseFloat(root.dataset.pickupCostValue || '0') || 0;

  // ---- i18n -------------------------------------------------------------
  // Self-contained dictionary (assets/returns-i18n.js) selected by storefront
  // locale. Avoids Shopify stripping custom keys from secondary locale files.
  const I18N = window.RAG_RETURNS_I18N || {};
  const RAW_LANG = (root.dataset.lang || 'pt').toLowerCase();
  const LANG = I18N[RAW_LANG] ? RAW_LANG
    : (I18N[RAW_LANG.slice(0, 2)] ? RAW_LANG.slice(0, 2)
      : (I18N.pt ? 'pt' : 'en'));
  function t(key) {
    const L = I18N[LANG] || {};
    if (L[key] != null && L[key] !== '') return L[key];
    // Fall back to English, then Portuguese, then the raw key.
    if (I18N.en && I18N.en[key] != null && I18N.en[key] !== '') return I18N.en[key];
    if (I18N.pt && I18N.pt[key] != null) return I18N.pt[key];
    return key;
  }
  // Fill static markup: data-i18n -> textContent, data-i18n-ph -> placeholder,
  // data-i18n-html -> innerHTML. Called at the end once $$ is available.
  function applyStaticI18n() {
    // Scope to the whole section (the title/intro live outside #returns-app)
    const scope = root.closest('.returns-section') || document;
    scope.querySelectorAll('[data-i18n]').forEach((el) => { el.textContent = t(el.dataset.i18n); });
    scope.querySelectorAll('[data-i18n-ph]').forEach((el) => { el.setAttribute('placeholder', t(el.dataset.i18nPh)); });
    scope.querySelectorAll('[data-i18n-html]').forEach((el) => { el.innerHTML = t(el.dataset.i18nHtml); });
  }

  const state = {
    order: null,
    items: [],               // normalised, one entry per fulfillment-state line
    selected: {},            // item.id -> { qty, action }
    shipping_option: 'self', // return shipping: 'self' | 'pickup'
    refund_method: 'original', // 'original' | 'iban'
    exchangeMode: 'variant', // 'variant' (same product) | 'different' (cart)
    exchange_shipping: 'self', // exchange shipping: 'self' | 'pickup'
    exchange_items: [],      // cross-product replacements imported from cart
    sizeSwaps: {},           // item.id -> replacement variant (same product, new size/colour)
    pickup: null,            // { mode: 'deduct' | 'purchase' } when shipping_option === 'pickup'
    exchange_refund_method: 'original', // refund method when the exchange balance favours the customer
    exchange_balance: null   // { amount, direction } computed from return vs replacement totals
  };

  // Set just before a submit so the success screen knows whether to show the
  // "buy pickup" call-to-action (purchase mode only).
  let pendingPickupPurchase = false;

  const productCache = {}; // handle -> /products/{handle}.js payload

  // ---- helpers ----------------------------------------------------------
  const $ = (sel, ctx) => (ctx || root).querySelector(sel);
  const $$ = (sel, ctx) => Array.from((ctx || root).querySelectorAll(sel));
  const esc = (s) => String(s == null ? '' : s).replace(/[&<>"']/g, (c) => ({
    '&': '&amp;', '<': '&lt;', '>': '&gt;', '"': '&quot;', "'": '&#39;'
  }[c]));
  const money = (v) => {
    const n = Number(v);
    if (isNaN(n)) return '';
    return n.toLocaleString('pt-PT', { style: 'currency', currency: 'EUR' });
  };
  const variantIdNumber = (gid) => String(gid || '').split('/').pop();
  function formatDate(iso) {
    if (!iso) return '';
    const d = new Date(iso);
    if (isNaN(d.getTime())) return String(iso);
    return d.toLocaleDateString(LANG === 'en' ? 'en-GB' : 'pt-PT',
      { day: '2-digit', month: '2-digit', year: 'numeric' });
  }

  // Legacy order-level delivery state, used only as a fallback when an item
  // doesn't carry its own fulfillment_state (old CRM payloads).
  function deliveryState(o) {
    const ds = (o.delivery_status || '').toUpperCase();
    if (ds) {
      if (ds === 'DELIVERED') return 'delivered';
      if (ds === 'UNFULFILLED') return 'unfulfilled';
      return 'in_transit';
    }
    if (o.fulfillment_status === 'UNFULFILLED') return 'unfulfilled';
    if (o.delivered === true) return 'delivered';
    return 'in_transit';
  }

  // ---- item model -------------------------------------------------------
  // The list of actions a single item allows, in display order.
  function allowedActions(it) {
    const a = [];
    if (it.can_cancel) a.push('cancel');
    if (it.can_return) a.push('return');
    if (it.can_exchange) a.push('exchange');
    if (it.can_refuse_delivery) a.push('refuse');
    return a;
  }

  // Ensure every item has a stable id, a fulfillment_state and per-item action
  // flags. When the CRM already provides the flags we trust them; otherwise we
  // derive them from the (legacy) order-level state so nothing breaks.
  function normalizeItems(order, rawItems) {
    const orderState = deliveryState(order);
    const withinDefault = order.within_14_days !== false; // unknown -> open
    return (rawItems || []).map((it) => {
      const id = it.id || it.line_item_id;
      const baseEligible = it.eligible !== false;
      const eligible = baseEligible && !INELIGIBLE_VARIANT_IDS.has(variantIdNumber(it.variant_id));
      const fs = it.fulfillment_state || orderState;
      const hasFlags = ['can_cancel', 'can_return', 'can_exchange', 'can_refuse_delivery']
        .some((k) => it[k] != null);
      let canCancel, canReturn, canExchange, canRefuse;
      if (hasFlags) {
        canCancel = !!it.can_cancel; canReturn = !!it.can_return;
        canExchange = !!it.can_exchange; canRefuse = !!it.can_refuse_delivery;
      } else {
        const windowOpen = it.return_window_open != null ? !!it.return_window_open : withinDefault;
        canCancel = fs === 'unfulfilled';
        canExchange = fs === 'unfulfilled' || (fs === 'delivered' && windowOpen);
        canReturn = fs === 'delivered' && windowOpen;
        canRefuse = fs === 'in_transit';
      }
      if (!eligible) { canCancel = canReturn = canExchange = canRefuse = false; }
      return Object.assign({}, it, {
        id: id,
        line_item_id: it.line_item_id || id,
        eligible: eligible,
        fulfillment_state: fs,
        can_cancel: canCancel,
        can_return: canReturn,
        can_exchange: canExchange,
        can_refuse_delivery: canRefuse
      });
    });
  }

  // Group items by fulfillment state (fixed order), sub-grouped by shipment
  // (fulfillment_name) so each parcel shows its own tracking header.
  function buildGroups(items) {
    const groups = [];
    GROUP_ORDER.forEach((stateKey) => {
      const inState = items.filter((it) => it.fulfillment_state === stateKey);
      if (!inState.length) return;
      const names = [];
      inState.forEach((it) => {
        const n = it.fulfillment_name || '';
        if (!names.includes(n)) names.push(n);
      });
      names.forEach((n) => {
        const rows = inState.filter((it) => (it.fulfillment_name || '') === n);
        groups.push({
          state: stateKey,
          name: n,
          tracking: (rows.find((it) => it.tracking_number) || {}).tracking_number || '',
          items: rows
        });
      });
    });
    // Anything with an unexpected state -> a trailing group so it's never hidden.
    const known = new Set(GROUP_ORDER);
    const rest = items.filter((it) => !known.has(it.fulfillment_state));
    if (rest.length) groups.push({ state: 'delivered', name: '', tracking: '', items: rest });
    return groups;
  }

  // Convenience selectors over the current selection.
  function selectedIds() { return Object.keys(state.selected); }
  function itemById(id) { return state.items.find((x) => x.id === id); }
  function selQty(it) { const s = state.selected[it.id]; return (s && s.qty) || 1; }
  function selAction(it) { const s = state.selected[it.id]; return s && s.action; }
  function exchangeItemsSel() { return state.items.filter((it) => selAction(it) === 'exchange'); }
  function carriedItemsSel() {
    return state.items.filter((it) => { const a = selAction(it); return a && a !== 'exchange'; });
  }
  function selectedActionSet() {
    const s = new Set();
    selectedIds().forEach((id) => { const a = state.selected[id].action; if (a) s.add(a); });
    return s;
  }

  function payloadItem(it, action, qty) {
    return {
      id: it.id,
      line_item_id: it.line_item_id,
      fulfillment_state: it.fulfillment_state,
      action: ACTION_TOKEN[action] || action,
      title: it.title,
      variant_title: it.variant_title,
      quantity: qty != null ? qty : selQty(it),
      price: it.price,
      sku: it.sku,
      image: it.image
    };
  }

  // ---- step navigation --------------------------------------------------
  function showStep(name) {
    $$('.returns-step').forEach((el) => el.classList.toggle('is-active', el.dataset.step === name));
    const order = ['identify', 'choose', 'exchange'];
    $$('.returns-steps-nav .step-pill').forEach((pill) => {
      const idx = order.indexOf(pill.dataset.for);
      const cur = order.indexOf(name);
      pill.classList.toggle('is-active', pill.dataset.for === name);
      pill.classList.toggle('is-done', idx > -1 && cur > -1 && idx < cur);
    });
    window.scrollTo({ top: root.offsetTop - 80, behavior: 'smooth' });
  }

  // Click a completed step pill to jump back to it
  $$('.returns-steps-nav .step-pill').forEach((pill) => {
    pill.addEventListener('click', () => {
      if (pill.classList.contains('is-done')) showStep(pill.dataset.for);
    });
  });

  function setExchangePillVisible(visible) {
    const pill = $$('.returns-steps-nav .step-pill').find((p) => p.dataset.for === 'exchange');
    if (pill) pill.style.display = visible ? '' : 'none';
  }

  // ---- Screen 1: lookup -------------------------------------------------
  const lookupForm = $('#returns-lookup-form');
  const lookupBtn = $('#returns-lookup-btn');
  const lookupError = $('#returns-lookup-error');

  async function fetchLookup(order_number, email) {
    const res = await fetch(API.lookup, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ order_number, email })
    });
    const data = await res.json().catch(() => ({}));
    return { res, data };
  }

  lookupForm.addEventListener('submit', async (e) => {
    e.preventDefault();
    lookupError.style.display = 'none';

    const orderInput = $('#returns-order-number');
    const emailInput = $('#returns-email');
    const order_number = orderInput.value.trim().replace(/^#/, '');
    const email = emailInput.value.trim();

    let ok = true;
    [orderInput, emailInput].forEach((i) => i.classList.remove('error'));
    if (!order_number) { orderInput.classList.add('error'); ok = false; }
    if (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(email)) { emailInput.classList.add('error'); ok = false; }
    if (!ok) return;

    setLoading(lookupBtn, true);
    try {
      const { res, data } = await fetchLookup(order_number, email);

      if (!res.ok || !data.found) {
        showLookupError(data.error || t('err_not_found'));
        return;
      }

      state.order = data.order || {};

      // One request per order: if there's already a request, show it and stop.
      if (data.existing_request) {
        showExistingRequest(data.existing_request);
        return;
      }

      state.items = normalizeItems(state.order, data.items);
      state.selected = {};
      state.exchange_items = [];
      state.sizeSwaps = {};
      renderChoose();
      showStep('choose');
    } catch (err) {
      console.error('returns-lookup error:', err);
      showLookupError(t('err_comm'));
    } finally {
      setLoading(lookupBtn, false);
    }
  });

  function showLookupError(msg) {
    lookupError.textContent = msg;
    lookupError.style.display = 'block';
  }

  // ---- Existing request (terminal notice) -------------------------------
  const REQ_TYPE_LABEL = {
    return: 'reqtype_return', exchange: 'reqtype_exchange', cancellation: 'reqtype_cancellation',
    withdrawal: 'reqtype_withdrawal', mixed: 'reqtype_mixed'
  };
  function showExistingRequest(req) {
    req = req || {};
    const nav = $('.returns-steps-nav');
    if (nav) nav.style.display = 'none';
    $$('.returns-step').forEach((el) => el.classList.remove('is-active'));

    const typeKey = REQ_TYPE_LABEL[(req.type || '').toLowerCase()];
    const parts = [];

    // Reference chip + status badge on one row
    const head = [];
    if (req.request_id) {
      head.push(`<span class="rex-ref"><span class="rex-ref-label">${esc(t('existing_ref'))}</span>` +
        `<span class="returns-ref rex-ref-code">${esc(req.request_id)}</span></span>`);
    }
    if (req.status) {
      const sc = String(req.status_code || '').toLowerCase();
      const tone = /reject|cancel|denied|recus/.test(sc) ? 'no'
        : (/approv|done|complete|conclu|refund/.test(sc) ? 'ok' : 'pending');
      head.push(`<span class="rex-status rex-status-${tone}">${esc(req.status)}</span>`);
    }
    if (head.length) parts.push(`<div class="rex-head">${head.join('')}</div>`);

    // Type · submitted date
    const meta = [];
    if (typeKey) meta.push(esc(t(typeKey)));
    if (req.created_at) meta.push(esc(t('existing_date')) + ' ' + esc(formatDate(req.created_at)));
    if (meta.length) parts.push(`<p class="rex-meta">${meta.join(' · ')}</p>`);

    // Items
    const items = Array.isArray(req.items) ? req.items : [];
    if (items.length) {
      parts.push(`<div class="rex-items"><p class="rex-items-title">${esc(t('existing_items_title'))}</p>` +
        '<ul>' + items.map((it) => {
          const meta2 = [it.variant_title, it.quantity ? `${t('qty')} ${it.quantity}` : '']
            .filter(Boolean).map(esc).join(' · ');
          return `<li><span class="rex-item-name">${esc(it.title || '')}</span>` +
            `${meta2 ? `<span class="rex-item-meta">${meta2}</span>` : ''}</li>`;
        }).join('') + '</ul></div>');
    }

    const body = $('#returns-existing-body');
    if (body) body.innerHTML = parts.join('');
    $('#returns-existing').style.display = 'block';
    window.scrollTo({ top: root.offsetTop - 80, behavior: 'smooth' });
  }

  // ---- Screen 2: grouped items + per-item actions -----------------------
  function renderChoose() {
    const o = state.order;
    const payBadge = o.financial_status === 'PAID' ? t('badge_paid') : (o.financial_status || '—');
    const states = new Set(state.items.map((it) => it.fulfillment_state));
    const partial = states.size > 1;

    $('#returns-order-summary').innerHTML = `
      <span><strong>${esc(t('l_order'))}</strong> ${esc(o.name || '')}</span>
      <span><strong>${esc(t('l_email'))}</strong> ${esc(o.email || '')}</span>
      <span>${esc(t('l_payment'))} <span class="returns-badge ${o.financial_status === 'PAID' ? 'ok' : ''}">${esc(payBadge)}</span></span>
      ${partial ? `<span class="returns-badge">${esc(t('badge_partial'))}</span>` : ''}
    `;

    // Any actionable item at all?
    const actionable = state.items.some((it) => it.eligible && allowedActions(it).length > 0);

    // Always show the products (read-only when nothing can be done with them).
    const groupsWrap = $('#returns-groups');
    groupsWrap.innerHTML = buildGroups(state.items).map(renderGroup).join('');
    groupsWrap.classList.toggle('is-readonly', !actionable);

    // Highlighted notice + hide the interactive bits when there are no actions.
    $('#returns-no-actions').style.display = actionable ? 'none' : 'flex';
    const introEl = $('#returns-choose-intro');
    if (introEl) introEl.style.display = actionable ? 'block' : 'none';
    const notesBlock = $('#returns-notes-block');
    if (notesBlock) notesBlock.style.display = actionable ? 'block' : 'none';
    $('#returns-choose-continue').style.display = actionable ? '' : 'none';

    // Reset dependent blocks
    $('#returns-shipping-block').style.display = 'none';
    $('#returns-refund-block').style.display = 'none';
    $('#returns-choose-continue').disabled = true;
    state.selected = {};
    setExchangePillVisible(false);
    updateContinueState();
    updateSelectAllState();
  }

  function renderGroup(g) {
    const header = [`<span class="group-state">${esc(t(GROUP_LABEL[g.state] || 'grp_delivered'))}</span>`];
    if (g.name) header.push(`<span class="group-name">${esc(g.name)}</span>`);
    if (g.tracking) header.push(`<span class="group-tracking">${esc(t('grp_tracking'))} ${esc(g.tracking)}</span>`);
    // Delivered shipment with a recorded delivery date: show it, and flag the
    // return window as expired when the CRM closed it (no date -> nothing shown).
    if (g.state === 'delivered') {
      const dated = g.items.find((it) => it.fulfillment_state === 'delivered' && it.delivered_at);
      if (dated) {
        header.push(`<span class="group-date">${esc(t('grp_delivered_on'))} ${esc(formatDate(dated.delivered_at))}</span>`);
        const expired = g.items.some((it) => it.eligible && it.delivered_at &&
          it.return_window_open === false && !it.can_return && !it.can_exchange);
        if (expired) header.push(`<span class="group-expired">${esc(t('grp_window_expired'))}</span>`);
      }
    }
    return `<div class="returns-group" data-state="${esc(g.state)}">
      <div class="returns-group-head">${header.join('')}</div>
      ${g.items.map(renderItem).join('')}
    </div>`;
  }

  function renderItem(it) {
    const acts = allowedActions(it);
    const disabled = !it.eligible || acts.length === 0;
    const img = it.image
      ? `<img class="item-img" src="${esc(it.image)}" alt="${esc(it.title)}" loading="lazy">`
      : `<div class="item-img"></div>`;
    const meta = [it.variant_title, it.sku].filter(Boolean).map(esc).join(' · ');
    // Only flag a per-item reason when the item itself is ineligible (e.g. embroidery).
    // An out-of-window / already-shipped order is explained by the global notice.
    const reason = !it.eligible ? (it.reason || t('item_ineligible')) : '';
    const qtyCtl = (!disabled && it.quantity > 1)
      ? `<label>${esc(t('qty'))}</label><input type="number" class="item-qty-input" min="1" max="${it.quantity}" value="1" disabled>`
      : '';
    const actionsHtml = disabled ? '' : actionSelector(it, acts);
    return `
      <div class="returns-item ${disabled ? 'is-disabled' : ''}" data-id="${esc(it.id)}" data-variant="${esc(variantIdNumber(it.variant_id))}">
        <input type="checkbox" class="item-check" ${disabled ? 'disabled' : ''} aria-label="${esc(it.title)}">
        ${img}
        <div class="item-body">
          <p class="item-title">${esc(it.title)}</p>
          <p class="item-meta">${meta}${it.quantity > 1 ? ` · ${esc(t('qty_ordered'))} ${it.quantity}` : ''}</p>
          ${reason ? `<span class="item-reason">${esc(reason)}</span>` : ''}
          <div class="item-actions" style="display:none;">${actionsHtml}</div>
        </div>
        <div class="item-qty">
          <span class="item-price">${money(it.price)}</span>
          ${qtyCtl}
        </div>
      </div>`;
  }

  function actionSelector(it, acts) {
    if (acts.length === 1) {
      return `<p class="item-action-single">${esc(t('pick_action_single'))} <strong>${esc(t(ACTION_LABEL[acts[0]]))}</strong></p>`;
    }
    const name = 'act-' + it.id;
    const opts = acts.map((a) =>
      `<label class="item-action-opt"><input type="radio" name="${esc(name)}" value="${a}"><span>${esc(t(ACTION_LABEL[a]))}</span></label>`
    ).join('');
    return `<p class="item-action-q">${esc(t('pick_action'))}</p><div class="item-action-row">${opts}</div>`;
  }

  // delegated events on the grouped items
  const groupsEl = $('#returns-groups');
  groupsEl.addEventListener('change', (e) => {
    const row = e.target.closest('.returns-item');
    if (!row) return;
    const id = row.dataset.id;

    if (e.target.classList.contains('item-check')) {
      const qtyInput = row.querySelector('.item-qty-input');
      const actionsBox = row.querySelector('.item-actions');
      if (e.target.checked) {
        row.classList.add('is-selected');
        const it = itemById(id);
        const acts = allowedActions(it);
        const action = acts.length === 1 ? acts[0] : null;
        state.selected[id] = { qty: qtyInput ? parseInt(qtyInput.value, 10) || 1 : 1, action: action };
        if (qtyInput) qtyInput.disabled = false;
        if (actionsBox) actionsBox.style.display = 'block';
      } else {
        row.classList.remove('is-selected');
        if (qtyInput) qtyInput.disabled = true;
        if (actionsBox) actionsBox.style.display = 'none';
        const radios = row.querySelectorAll('input[type="radio"]');
        radios.forEach((r) => { r.checked = false; });
        delete state.selected[id];
      }
      updateActionBlocks();
      updateContinueState();
      updateSelectAllState();
      return;
    }

    if (e.target.type === 'radio' && e.target.name.indexOf('act-') === 0) {
      if (state.selected[id]) state.selected[id].action = e.target.value;
      updateActionBlocks();
      updateContinueState();
      return;
    }

    if (e.target.classList.contains('item-qty-input')) {
      if (state.selected[id]) state.selected[id].qty = parseInt(e.target.value, 10) || 1;
      updatePickupMode();
    }
  });

  // Let the item body toggle its checkbox (not the action controls or qty).
  groupsEl.addEventListener('click', (e) => {
    const row = e.target.closest('.returns-item');
    if (!row || row.classList.contains('is-disabled')) return;
    if (e.target.classList.contains('item-check') || e.target.closest('.item-qty') ||
        e.target.closest('.item-actions')) return;
    const cb = row.querySelector('.item-check');
    if (!cb || cb.disabled) return;
    cb.checked = !cb.checked;
    cb.dispatchEvent(new Event('change', { bubbles: true }));
  });

  // ---- select all -------------------------------------------------------
  // Rows that can actually be selected (eligible + at least one action).
  function selectableRows() {
    return $$('.returns-item', groupsEl).filter((r) => {
      const cb = r.querySelector('.item-check');
      return cb && !cb.disabled;
    });
  }

  function setAllSelected(check) {
    selectableRows().forEach((row) => {
      const id = row.dataset.id;
      const it = itemById(id);
      const cb = row.querySelector('.item-check');
      const qtyInput = row.querySelector('.item-qty-input');
      const actionsBox = row.querySelector('.item-actions');
      if (check) {
        cb.checked = true;
        row.classList.add('is-selected');
        const existing = state.selected[id];
        const acts = allowedActions(it);
        state.selected[id] = {
          qty: existing ? existing.qty : (qtyInput ? parseInt(qtyInput.value, 10) || 1 : 1),
          action: existing ? existing.action : (acts.length === 1 ? acts[0] : null)
        };
        if (qtyInput) qtyInput.disabled = false;
        if (actionsBox) actionsBox.style.display = 'block';
      } else {
        cb.checked = false;
        row.classList.remove('is-selected');
        if (qtyInput) qtyInput.disabled = true;
        if (actionsBox) actionsBox.style.display = 'none';
        row.querySelectorAll('input[type="radio"]').forEach((r) => { r.checked = false; });
        delete state.selected[id];
      }
    });
    updateActionBlocks();
    updateContinueState();
    updateSelectAllState();
  }

  // Reflect selection count on the master checkbox (checked / indeterminate)
  // and hide the control unless there's more than one selectable item.
  function updateSelectAllState() {
    const wrap = $('#returns-select-all-wrap');
    const master = $('#returns-select-all');
    if (!wrap || !master) return;
    const rows = selectableRows();
    wrap.style.display = rows.length > 1 ? 'flex' : 'none';
    const sel = rows.filter((r) => state.selected[r.dataset.id]).length;
    master.checked = rows.length > 0 && sel === rows.length;
    master.indeterminate = sel > 0 && sel < rows.length;
  }

  const selectAllMaster = $('#returns-select-all');
  if (selectAllMaster) selectAllMaster.addEventListener('change', () => setAllSelected(selectAllMaster.checked));

  // ---- dependent blocks (shipping / refund / notes) on step 2 -----------
  $('#returns-step-choose').addEventListener('change', (e) => {
    if (e.target.name === 'returns-shipping') {
      state.shipping_option = e.target.value;
      toggleShipDetail($('#returns-shipping-block'), e.target.value);
      updatePickupMode();
    }
    if (e.target.name === 'returns-pickup-same') syncPickupAddrVisibility();
    if (e.target.name === 'returns-refund') {
      state.refund_method = e.target.value;
      $('#returns-iban-fields').style.display = e.target.value === 'iban' ? 'grid' : 'none';
    }
  });
  $('#returns-step-choose').addEventListener('input', (e) => {
    if (e.target.classList && e.target.classList.contains('error')) e.target.classList.remove('error');
  });

  function toggleShipDetail(block, value) {
    if (!block) return;
    $$('.ship-detail', block).forEach((d) => { d.style.display = d.dataset.ship === value ? 'block' : 'none'; });
  }

  // Refund policy from the order's payment gateway.
  function refundPolicy() {
    const gw = (state.order.payment_gateway || (state.order.gateway_names || [])[0] || '').toLowerCase();
    if (!gw) return 'unknown';
    return DIRECT_GATEWAYS.some((d) => gw.indexOf(d) !== -1) ? 'original' : 'iban';
  }

  function applyRefundPolicyTo(block, radioName, ibanFieldsSel, setMethod) {
    const origRadio = $(`input[name="${radioName}"][value="original"]`, block);
    const ibanRadio = $(`input[name="${radioName}"][value="iban"]`, block);
    const origOpt = origRadio.closest('.returns-radio-opt');
    const ibanOpt = ibanRadio.closest('.returns-radio-opt');
    const origDesc = origOpt.querySelector('.opt-desc');
    const ibanDesc = ibanOpt.querySelector('.opt-desc');
    const policy = refundPolicy();

    origRadio.disabled = false; ibanRadio.disabled = false;
    origOpt.classList.remove('is-disabled'); ibanOpt.classList.remove('is-disabled');

    if (policy === 'original') {
      ibanRadio.disabled = true; ibanOpt.classList.add('is-disabled');
      origRadio.checked = true; setMethod('original');
      origDesc.textContent = t('rf_orig_original');
      ibanDesc.textContent = t('rf_iban_unavailable');
      $(ibanFieldsSel).style.display = 'none';
    } else if (policy === 'iban') {
      origRadio.disabled = true; origOpt.classList.add('is-disabled');
      ibanRadio.checked = true; setMethod('iban');
      origDesc.textContent = t('rf_orig_ibanonly');
      ibanDesc.textContent = t('rf_iban_required');
      $(ibanFieldsSel).style.display = 'grid';
    } else {
      origDesc.textContent = t('rf_orig_unknown');
      ibanDesc.textContent = t('rf_iban_unknown');
    }
  }
  function applyRefundPolicy() {
    applyRefundPolicyTo($('#returns-refund-block'), 'returns-refund', '#returns-iban-fields',
      (m) => { state.refund_method = m; });
  }
  function applyExchangeRefundPolicy() {
    applyRefundPolicyTo($('#exchange-refund-block'), 'exchange-refund', '#exchange-iban-fields',
      (m) => { state.exchange_refund_method = m; });
  }

  function pickupAllowed() {
    const c = (state.order.shipping_country_code || '').toUpperCase();
    if (!c) return true;
    return c === 'PT' || c === 'ES';
  }
  function applyPickupConstraint(block) {
    if (!block) return;
    const pickup = $('input[value="pickup"]', block);
    const self = $('input[value="self"]', block);
    if (!pickup) return;
    const opt = pickup.closest('.returns-radio-opt');
    if (pickupAllowed()) {
      pickup.disabled = false; opt.classList.remove('is-disabled');
    } else {
      pickup.disabled = true; opt.classList.add('is-disabled');
      const desc = opt.querySelector('.opt-desc');
      if (desc) desc.textContent = t('pickup_country_only');
      if (self) self.checked = true;
      toggleShipDetail(block, 'self');
    }
  }

  // Shows/hides shipping + refund based on the set of chosen actions.
  function updateActionBlocks() {
    const actions = selectedActionSet();
    const paid = state.order.financial_status === 'PAID';
    const hasReturn = actions.has('return');
    const hasExchange = actions.has('exchange');
    const hasMoneyBack = actions.has('cancel') || actions.has('return') || actions.has('refuse');

    // Shipping: only physical returns of delivered goods (self/pickup).
    // Exchanges collect shipping on step 3; cancel/refuse need none.
    $('#returns-shipping-block').style.display = hasReturn ? 'block' : 'none';
    if (hasReturn) {
      applyPickupConstraint($('#returns-shipping-block'));
      const checked = $('input[name="returns-shipping"]:checked');
      if (checked) state.shipping_option = checked.value;
    }
    updatePickupMode();

    // Refund: money back to the customer on a paid order (not exchange balance).
    const showRefund = hasMoneyBack && paid;
    $('#returns-refund-block').style.display = showRefund ? 'block' : 'none';
    if (showRefund) applyRefundPolicy();

    // Notes are collected on the exchange step (3) — hide the step-2 box for exchanges
    const notesBlock = $('#returns-notes-block');
    if (notesBlock) notesBlock.style.display = hasExchange ? 'none' : 'block';

    setExchangePillVisible(hasExchange);
    const btn = $('#returns-choose-continue');
    btn.querySelector('.btn-text').textContent = hasExchange ? t('continue_exchange') : t('continue_submit');
  }

  function updateContinueState() {
    const ids = selectedIds();
    const hasItems = ids.length > 0;
    const allHaveAction = ids.every((id) => !!state.selected[id].action);
    $('#returns-choose-continue').disabled = !(hasItems && allHaveAction);
    updatePickupMode();
  }

  // ---- Pickup (recolha) -------------------------------------------------
  // Value that would be refunded — sum of selected money-back items.
  function refundTotal() {
    return selectedIds().reduce((sum, id) => {
      const it = itemById(id);
      const a = state.selected[id].action;
      if (!it || a === 'exchange') return sum;
      return sum + Number(it.price) * (state.selected[id].qty || 0);
    }, 0);
  }

  function updatePickupMode() {
    const block = $('#returns-shipping-block');
    const deductEl = $('#returns-pickup-deduct');
    const buyEl = $('#returns-pickup-buy');
    if (!block || !deductEl || !buyEl) return;

    if (block.style.display === 'none' || state.shipping_option !== 'pickup') {
      deductEl.style.display = 'none';
      buyEl.style.display = 'none';
      state.pickup = null;
      return;
    }

    const paid = state.order.financial_status === 'PAID';
    const canDeduct = paid && PICKUP_COST_VALUE > 0 && refundTotal() >= PICKUP_COST_VALUE;
    if (canDeduct) {
      deductEl.style.display = 'block';
      buyEl.style.display = 'none';
      state.pickup = { mode: 'deduct' };
      syncPickupAddrVisibility();
    } else {
      deductEl.style.display = 'none';
      buyEl.style.display = 'block';
      state.pickup = { mode: 'purchase' };
    }
  }

  function syncPickupAddrVisibility() {
    const sameNo = $('input[name="returns-pickup-same"][value="no"]');
    const addr = $('#returns-pickup-addr');
    if (addr) addr.style.display = (sameNo && sameNo.checked) ? 'block' : 'none';
  }

  function fieldVal(id) {
    const el = document.getElementById(id);
    return el ? el.value.trim() : '';
  }
  function markField(el, invalid) {
    if (el) el.classList.toggle('error', !!invalid);
  }

  // Build the pickup payload for a submit. Returns null when pickup wasn't chosen.
  function buildPickupPayload(context) {
    const shipping = context === 'return' ? state.shipping_option : state.exchange_shipping;
    if (shipping !== 'pickup') return null;
    const mode = context === 'return' ? ((state.pickup && state.pickup.mode) || 'purchase') : 'purchase';
    const p = { mode: mode, cost: PICKUP_COST_VALUE, cost_display: PICKUP_COST_DISPLAY };
    if (mode === 'deduct') {
      const sameNo = $('input[name="returns-pickup-same"][value="no"]');
      const sameAddress = !(sameNo && sameNo.checked);
      p.same_address = sameAddress;
      if (!sameAddress) {
        p.address = {
          name: fieldVal('returns-pickup-name'),
          phone: fieldVal('returns-pickup-phone'),
          line: fieldVal('returns-pickup-line'),
          postal_code: fieldVal('returns-pickup-postal'),
          city: fieldVal('returns-pickup-city')
        };
      }
      p.preferred_day = fieldVal('returns-pickup-day');
    }
    return p;
  }

  $('#returns-choose-back').addEventListener('click', () => showStep('identify'));

  $('#returns-choose-continue').addEventListener('click', () => {
    const ids = selectedIds();
    if (!ids.length || !ids.every((id) => state.selected[id].action)) return;
    if (selectedActionSet().has('exchange')) {
      renderExchange();
      showStep('exchange');
    } else {
      submitMixed();
    }
  });

  // Validate pickup fields (deduct mode) and push invalid inputs to `invalid`.
  function validatePickup(pickup, invalid) {
    if (!pickup || pickup.mode !== 'deduct') return;
    const dayEl = $('#returns-pickup-day');
    markField(dayEl, !fieldVal('returns-pickup-day'));
    if (!fieldVal('returns-pickup-day')) invalid.push(dayEl);
    if (pickup.same_address === false) {
      [['returns-pickup-name', pickup.address.name],
       ['returns-pickup-phone', pickup.address.phone],
       ['returns-pickup-line', pickup.address.line],
       ['returns-pickup-postal', pickup.address.postal_code],
       ['returns-pickup-city', pickup.address.city]
      ].forEach(([id, val]) => {
        const el = document.getElementById(id);
        markField(el, !val);
        if (!val) invalid.push(el);
      });
    }
  }

  // Non-exchange submit: cancel / return / refuse, possibly mixed together.
  async function submitMixed() {
    const o = state.order;
    const paid = o.financial_status === 'PAID';
    const actions = selectedActionSet();
    const hasReturn = actions.has('return');
    const hasMoneyBack = actions.has('cancel') || actions.has('return') || actions.has('refuse');
    const invalid = [];

    // Refund (money-back on a paid order)
    let refund = { method: 'original' };
    if (paid && hasMoneyBack) {
      refund = { method: state.refund_method };
      if (state.refund_method === 'iban') {
        const ibanEl = $('#returns-iban');
        const holderEl = $('#returns-iban-holder');
        const iban = ibanEl.value.trim();
        const holder = holderEl.value.trim();
        markField(ibanEl, !iban); if (!iban) invalid.push(ibanEl);
        markField(holderEl, !holder); if (!holder) invalid.push(holderEl);
        refund.iban = iban;
        refund.holder = holder;
      }
    }

    // Shipping only relevant for physical returns.
    const pickup = hasReturn ? buildPickupPayload('return') : null;
    validatePickup(pickup, invalid);

    if (invalid.length) {
      showChooseError(t('err_required_fields'));
      if (invalid[0] && invalid[0].focus) invalid[0].focus();
      return;
    }

    const uniqueActions = [...actions];
    const type = uniqueActions.length === 1 ? SINGLE_TYPE[uniqueActions[0]] : 'mixed';
    let shipping = 'self';
    if (type === 'withdrawal') shipping = 'refuse_delivery';
    else if (hasReturn) shipping = state.shipping_option;

    pendingPickupPurchase = !!(pickup && pickup.mode === 'purchase');

    const items = selectedIds().map((id) => {
      const it = itemById(id);
      return payloadItem(it, state.selected[id].action, state.selected[id].qty);
    });

    const payload = {
      order_id: o.id,
      order_number: String(o.name || '').replace(/^#/, ''),
      customer_email: o.email,
      customer_name: customerName || '',
      type: type,
      shipping_option: shipping,
      refund: paid ? refund : { method: 'original' },
      items: items,
      notes: $('#returns-notes').value.trim()
    };
    if (pickup) payload.pickup = pickup;

    await postSubmit(API.submit, payload, $('#returns-choose-continue'), showChooseError);
  }

  function showChooseError(msg) {
    const box = $('#returns-choose-error');
    box.textContent = msg;
    box.style.display = 'block';
    box.scrollIntoView({ behavior: 'smooth', block: 'center' });
  }

  // ---- Screen 3: exchange -----------------------------------------------
  function renderExchange() {
    const exItems = exchangeItemsSel();
    const allPredispatch = exItems.length > 0 && exItems.every((it) => it.fulfillment_state === 'unfulfilled');
    const recapTitle = $('#exchange-recap-title');
    if (recapTitle) recapTitle.textContent = allPredispatch ? t('exch_recap_predispatch') : t('exch_recap_return');

    // Drop swaps for items no longer being exchanged
    Object.keys(state.sizeSwaps).forEach((id) => {
      if (!state.selected[id] || state.selected[id].action !== 'exchange') delete state.sizeSwaps[id];
    });

    // Read-only recap of the items being swapped out
    $('#exchange-return-recap').innerHTML = exItems.map((it) => `
      <div class="returns-item" style="flex-wrap:wrap;">
        ${it.image ? `<img class="item-img" src="${esc(it.image)}" alt="${esc(it.title)}" loading="lazy">` : '<div class="item-img"></div>'}
        <div class="item-body">
          <p class="item-title">${esc(it.title)}</p>
          <p class="item-meta">${[it.variant_title, it.sku].filter(Boolean).map(esc).join(' · ')} · ${esc(t('qty'))} ${selQty(it)}</p>
        </div>
        <span class="item-price">${money(it.price)}</span>
      </div>
    `).join('');

    // Items carried along in this same request (cancel / return / refuse)
    const carried = carriedItemsSel();
    const carriedBox = $('#exchange-carried');
    if (carriedBox) {
      if (carried.length) {
        carriedBox.style.display = 'block';
        carriedBox.innerHTML = `<p class="returns-hint"><strong>${esc(t('exch_also_title'))}</strong></p>` +
          carried.map((it) => `<div class="returns-item" style="flex-wrap:wrap;">
            ${it.image ? `<img class="item-img" src="${esc(it.image)}" alt="${esc(it.title)}" loading="lazy">` : '<div class="item-img"></div>'}
            <div class="item-body">
              <p class="item-title">${esc(it.title)}</p>
              <p class="item-meta">${[it.variant_title, it.sku].filter(Boolean).map(esc).join(' · ')} · ${esc(t('qty'))} ${selQty(it)}</p>
            </div>
            <span class="returns-badge">${esc(t(ACTION_LABEL[state.selected[it.id].action]))}</span>
          </div>`).join('');
      } else {
        carriedBox.style.display = 'none';
        carriedBox.innerHTML = '';
      }
    }

    // Variant pickers (one per exchanged item) for the "same product" mode
    $('#exchange-variant-items').innerHTML = exItems.map((it) => `
      <div class="returns-item" style="flex-wrap:wrap;">
        ${it.image ? `<img class="item-img" src="${esc(it.image)}" alt="${esc(it.title)}" loading="lazy">` : '<div class="item-img"></div>'}
        <div class="item-body">
          <p class="item-title">${esc(it.title)}</p>
          <p class="item-meta">${[it.variant_title, it.sku].filter(Boolean).map(esc).join(' · ')}</p>
        </div>
        <div class="variant-swap" data-id="${esc(it.id)}">
          <span class="returns-hint">${esc(t('vs_loading'))}</span>
        </div>
      </div>
    `).join('');
    exItems.forEach((it) => buildVariantSwap(it));

    // Reset mode to the most common case (same product)
    state.exchangeMode = 'variant';
    const modeVariant = $$('input[name="exchange-mode"]').find((r) => r.value === 'variant');
    if (modeVariant) modeVariant.checked = true;
    applyExchangeMode();

    // Shipping only matters when an exchanged item was already delivered
    const showShip = exItems.some((it) => it.fulfillment_state === 'delivered');
    $('#exchange-shipping-block').style.display = showShip ? 'block' : 'none';
    state.exchange_shipping = 'self';
    const shipSelf = $$('input[name="exchange-shipping"]').find((r) => r.value === 'self');
    if (shipSelf) shipSelf.checked = true;
    toggleShipDetail($('#exchange-shipping-block'), 'self');
    if (showShip) {
      applyPickupConstraint($('#exchange-shipping-block'));
      const checked = $('input[name="exchange-shipping"]:checked');
      if (checked) state.exchange_shipping = checked.value;
    }

    renderExchangeItems();
    updateExchangeSubmitState();
  }

  function applyExchangeMode() {
    const variant = state.exchangeMode === 'variant';
    $('#exchange-variant-block').style.display = variant ? 'block' : 'none';
    $('#exchange-cart-block').style.display = variant ? 'none' : 'block';
    if (variant) {
      state.exchange_items = [];
      renderExchangeItems();
      $$('#exchange-variant-items .variant-swap').forEach((box) => {
        if (box._prod) updateVariantSwap(box, box.dataset.id);
      });
    } else {
      state.sizeSwaps = {};
    }
    updateExchangeSubmitState();
  }

  const exchangeStepEl = $$('.returns-step').find((s) => s.dataset.step === 'exchange');
  exchangeStepEl.addEventListener('change', (e) => {
    if (e.target.name === 'exchange-mode') {
      state.exchangeMode = e.target.value;
      applyExchangeMode();
    }
    if (e.target.name === 'exchange-shipping') {
      state.exchange_shipping = e.target.value;
      toggleShipDetail($('#exchange-shipping-block'), e.target.value);
    }
    if (e.target.name === 'exchange-refund') {
      state.exchange_refund_method = e.target.value;
      $('#exchange-iban-fields').style.display = e.target.value === 'iban' ? 'grid' : 'none';
    }
  });
  exchangeStepEl.addEventListener('input', (e) => {
    if (e.target.classList && e.target.classList.contains('error')) e.target.classList.remove('error');
  });

  async function loadProduct(handle) {
    if (productCache[handle]) return productCache[handle];
    const res = await fetch('/products/' + handle + '.js');
    if (!res.ok) return null;
    const data = await res.json();
    productCache[handle] = data;
    return data;
  }

  async function resolveHandle(full) {
    if (full.product_handle) return full.product_handle;
    const wantVid = variantIdNumber(full.variant_id);
    const terms = [full.sku, full.title].filter(Boolean);
    for (const term of terms) {
      try {
        const res = await fetch('/search/suggest.json?q=' + encodeURIComponent(term) +
          '&resources[type]=product&resources[limit]=6');
        const data = await res.json();
        const products = (((data.resources || {}).results || {}).products) || [];
        for (const p of products) {
          const prod = await loadProduct(p.handle);
          if (prod && prod.variants.some((v) => String(v.id) === String(wantVid))) return p.handle;
        }
      } catch (err) { /* keep trying */ }
    }
    return null;
  }

  function swapBox(id) {
    return $$('.variant-swap').find((b) => b.dataset.id === id);
  }

  async function buildVariantSwap(item) {
    const box = swapBox(item.id);
    if (!box) return;
    const full = itemById(item.id) || {};
    const handle = await resolveHandle(full);
    if (!handle) {
      box.innerHTML = `<p class="returns-hint">${esc(t('vs_fallback_cart'))}</p>`;
      return;
    }
    const prod = await loadProduct(handle);
    if (!prod || !prod.variants || prod.variants.length <= 1) {
      box.innerHTML = `<p class="returns-hint">${esc(t('vs_no_variants'))}</p>`;
      return;
    }
    box._prod = prod;
    box._returnQty = selQty(full);

    const current = prod.variants.find((v) => String(v.id) === String(variantIdNumber(full.variant_id)));
    const optionNames = prod.options.map((o) => (typeof o === 'string' ? o : o.name));

    const fields = optionNames.map((name, idx) => {
      const values = [...new Set(prod.variants.map((v) => v['option' + (idx + 1)]))];
      const opts = values.map((val) =>
        `<option value="${esc(val)}"${current && current['option' + (idx + 1)] === val ? ' selected' : ''}>${esc(val)}</option>`
      ).join('');
      return `<div class="vopt-field">
          <label class="returns-label">${esc(name)}</label>
          <select class="vopt returns-input" data-opt="${idx + 1}">${opts}</select>
        </div>`;
    }).join('');

    box.innerHTML = `<p class="variant-swap-title">${esc(t('vs_title'))}</p>
      <div class="vopt-row">${fields}</div>
      <p class="variant-swap-status returns-hint"></p>`;
    updateVariantSwap(box, item.id);
  }

  function updateVariantSwap(box, id) {
    const prod = box._prod;
    if (!prod) return;
    const chosen = $$('.vopt', box).map((s) => s.value);
    const v = prod.variants.find((variant) =>
      chosen.every((val, i) => (variant['option' + (i + 1)] || '') === val));
    const status = $('.variant-swap-status', box);
    const full = itemById(id) || {};
    const currentVid = variantIdNumber(full.variant_id);

    if (!v) {
      delete state.sizeSwaps[id];
      status.textContent = t('vs_unavailable');
      status.className = 'variant-swap-status returns-hint is-warn';
    } else if (!v.available) {
      delete state.sizeSwaps[id];
      status.textContent = t('vs_soldout');
      status.className = 'variant-swap-status returns-hint is-warn';
    } else if (String(v.id) === String(currentVid)) {
      delete state.sizeSwaps[id];
      status.textContent = t('vs_choosediff');
      status.className = 'variant-swap-status returns-hint';
    } else {
      state.sizeSwaps[id] = {
        variant_id: `gid://shopify/ProductVariant/${v.id}`,
        title: prod.title,
        variant_title: v.public_title || v.title || null,
        quantity: box._returnQty || 1,
        price: v.price / 100,
        sku: v.sku || '',
        image: (v.featured_image && v.featured_image.src) || prod.featured_image || null
      };
      status.textContent = t('vs_new') + ' ' + (v.public_title || v.title) + ' — ' + money(v.price / 100);
      status.className = 'variant-swap-status returns-hint is-ok';
    }
    updateExchangeSubmitState();
  }

  $('#exchange-variant-items').addEventListener('change', (e) => {
    if (!e.target.classList.contains('vopt')) return;
    const box = e.target.closest('.variant-swap');
    if (box) updateVariantSwap(box, box.dataset.id);
  });

  function updateExchangeSubmitState() {
    const has = state.exchangeMode === 'variant'
      ? Object.keys(state.sizeSwaps).length > 0
      : state.exchange_items.length > 0;
    $('#exchange-submit-btn').disabled = !has;
    updateExchangeBalance();
  }

  // Value of exchanged items vs. replacements (pickup excluded).
  function exchangeTotals() {
    const returnTotal = exchangeItemsSel()
      .reduce((s, it) => s + Number(it.price) * (selQty(it) || 0), 0);
    const list = state.exchangeMode === 'variant'
      ? Object.values(state.sizeSwaps) : state.exchange_items;
    const exchangeTotal = list.reduce((s, it) => s + Number(it.price) * (it.quantity || 0), 0);
    return {
      returnTotal: returnTotal,
      exchangeTotal: exchangeTotal,
      balance: Math.round((returnTotal - exchangeTotal) * 100) / 100
    };
  }

  function updateExchangeBalance() {
    const block = $('#exchange-balance-block');
    if (!block) return;
    const has = state.exchangeMode === 'variant'
      ? Object.keys(state.sizeSwaps).length > 0
      : state.exchange_items.length > 0;
    if (!has) { block.style.display = 'none'; state.exchange_balance = null; return; }

    const totals = exchangeTotals();
    const EPS = 0.005;
    let direction = 'even';
    let label = '';
    if (totals.balance > EPS) { direction = 'refund'; label = t('ex_balance_refund'); }
    else if (totals.balance < -EPS) { direction = 'pay'; label = t('ex_balance_pay'); }
    state.exchange_balance = {
      return_total: totals.returnTotal, exchange_total: totals.exchangeTotal,
      amount: totals.balance, direction: direction
    };

    const intro = direction === 'refund' ? t('ex_balance_intro_refund')
      : (direction === 'pay' ? t('ex_balance_intro_pay') : t('ex_balance_intro_even'));
    const calc = `${esc(t('ex_balance_returned'))} ${money(totals.returnTotal)} − ${esc(t('ex_balance_new'))} ${money(totals.exchangeTotal)}`;
    const summary = $('#exchange-balance-summary');
    if (direction === 'even') {
      summary.innerHTML = `<p class="exchange-balance-intro">${esc(intro)}</p>`;
    } else {
      summary.innerHTML = `<p class="exchange-balance-intro">${esc(intro)}</p>`
        + `<p class="exchange-balance-calc">${calc} = <strong>${money(Math.abs(totals.balance))} ${esc(label)}</strong></p>`;
    }

    const refundEl = $('#exchange-refund-block');
    const topayEl = $('#exchange-topay-note');
    const wasRefundVisible = refundEl.style.display === 'block';
    block.style.display = 'block';
    if (direction === 'refund') {
      refundEl.style.display = 'block';
      topayEl.style.display = 'none';
      if (!wasRefundVisible) applyExchangeRefundPolicy();
    } else if (direction === 'pay') {
      refundEl.style.display = 'none';
      topayEl.style.display = 'flex';
    } else {
      refundEl.style.display = 'none';
      topayEl.style.display = 'none';
    }
  }

  function renderExchangeItems() {
    const wrap = $('#exchange-items');
    if (state.exchange_items.length === 0) {
      wrap.innerHTML = `<p class="returns-empty-note">${esc(t('exch_cart_empty'))}</p>`;
    } else {
      wrap.innerHTML = state.exchange_items.map((it, i) => `
        <div class="returns-item is-selected" data-ex="${i}">
          ${it.image ? `<img class="item-img" src="${esc(it.image)}" alt="${esc(it.title)}" loading="lazy">` : '<div class="item-img"></div>'}
          <div class="item-body">
            <p class="item-title">${esc(it.title)}</p>
            <p class="item-meta">${[it.variant_title, it.sku].filter(Boolean).map(esc).join(' · ')}</p>
          </div>
          <div class="item-qty">
            <span class="item-price">${money(it.price)}</span>
            <label>${esc(t('qty'))}</label>
            <input type="number" class="ex-qty-input" min="1" value="${it.quantity}" data-ex="${i}">
          </div>
          <button type="button" class="returns-btn-ghost ex-remove" data-ex="${i}" aria-label="${esc(t('remove'))}" style="padding:6px 10px;">×</button>
        </div>
      `).join('');
    }
    updateExchangeSubmitState();
  }

  $('#exchange-import-btn').addEventListener('click', async () => {
    try {
      const res = await fetch('/cart.js');
      const cart = await res.json();
      if (!cart.items || cart.items.length === 0) {
        showExchangeError(t('exch_cart_empty_err'));
        return;
      }
      state.exchange_items = cart.items.map((item) => ({
        variant_id: `gid://shopify/ProductVariant/${item.variant_id}`,
        title: item.product_title,
        variant_title: item.variant_title || null,
        quantity: item.quantity,
        price: item.price / 100,
        sku: item.sku || '',
        image: item.image || null
      }));
      renderExchangeItems();
      const btn = $('#exchange-import-btn');
      btn.classList.add('import-success');
      setTimeout(() => btn.classList.remove('import-success'), 2000);
    } catch (err) {
      console.error('cart import error:', err);
      showExchangeError(t('exch_cart_error'));
    }
  });

  $('#exchange-items').addEventListener('input', (e) => {
    if (e.target.classList.contains('ex-qty-input')) {
      const i = parseInt(e.target.dataset.ex, 10);
      if (state.exchange_items[i]) state.exchange_items[i].quantity = parseInt(e.target.value, 10) || 1;
    }
  });
  $('#exchange-items').addEventListener('click', (e) => {
    const btn = e.target.closest('.ex-remove');
    if (!btn) return;
    state.exchange_items.splice(parseInt(btn.dataset.ex, 10), 1);
    renderExchangeItems();
  });

  $('#exchange-back').addEventListener('click', () => showStep('choose'));

  $('#exchange-submit-btn').addEventListener('click', () => {
    const replacements = state.exchangeMode === 'variant'
      ? Object.values(state.sizeSwaps)
      : state.exchange_items;
    if (replacements.length === 0) return;
    const o = state.order;
    const exItems = exchangeItemsSel();
    const carried = carriedItemsSel();
    const anyDelivered = exItems.some((it) => it.fulfillment_state === 'delivered');

    const totals = exchangeTotals();
    const direction = totals.balance > 0.005 ? 'refund'
      : (totals.balance < -0.005 ? 'pay' : 'even');

    // Refund details only when the balance favours the customer
    let refund = null;
    if (direction === 'refund') {
      refund = { method: state.exchange_refund_method };
      if (state.exchange_refund_method === 'iban') {
        const ibanEl = $('#exchange-iban');
        const holderEl = $('#exchange-iban-holder');
        const iban = ibanEl.value.trim();
        const holder = holderEl.value.trim();
        const invalid = [];
        markField(ibanEl, !iban); if (!iban) invalid.push(ibanEl);
        markField(holderEl, !holder); if (!holder) invalid.push(holderEl);
        if (invalid.length) {
          showExchangeError(t('err_required_fields'));
          if (invalid[0].focus) invalid[0].focus();
          return;
        }
        refund.iban = iban;
        refund.holder = holder;
      }
    }

    const pickup = anyDelivered ? buildPickupPayload('exchange') : null;
    pendingPickupPurchase = !!(pickup && pickup.mode === 'purchase');

    // Every item leaving the customer, each tagged with its action.
    const returnItems = exItems.map((it) => payloadItem(it, 'exchange', selQty(it)))
      .concat(carried.map((it) => payloadItem(it, state.selected[it.id].action, selQty(it))));

    const payload = {
      order_id: o.id,
      order_number: String(o.name || '').replace(/^#/, ''),
      customer_email: o.email,
      customer_name: customerName || '',
      type: carried.length ? 'mixed' : 'exchange',
      exchange_type: state.exchangeMode, // 'variant' | 'different'
      shipping_option: anyDelivered ? state.exchange_shipping : 'self',
      return_items: returnItems,
      exchange_items: replacements,
      balance: {
        return_total: totals.returnTotal,
        exchange_total: totals.exchangeTotal,
        amount: totals.balance,        // > 0 refund to customer, < 0 customer pays
        direction: direction           // 'refund' | 'pay' | 'even'
      },
      notes: $('#exchange-notes').value.trim()
    };
    if (refund) payload.refund = refund;
    if (pickup) payload.pickup = pickup;
    postSubmit(API.exchange, payload, $('#exchange-submit-btn'), showExchangeError);
  });

  function showExchangeError(msg) {
    const box = $('#exchange-error');
    box.textContent = msg;
    box.style.display = 'block';
    box.scrollIntoView({ behavior: 'smooth', block: 'center' });
  }

  // ---- shared submit ----------------------------------------------------
  async function postSubmit(url, payload, btn, errFn) {
    setLoading(btn, true);
    try {
      const res = await fetch(url, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(payload)
      });
      const data = await res.json().catch(() => ({}));
      if (res.ok && data.success) {
        showSuccess(data.return_id);
      } else if (res.status === 409 || data.error === 'already_exists') {
        // A request already exists for this order (e.g. double submit).
        await handleAlreadyExists(payload);
      } else {
        errFn(data.error || t('err_submit'));
      }
    } catch (err) {
      console.error('submit error:', err);
      errFn(t('err_comm'));
    } finally {
      setLoading(btn, false);
    }
  }

  // Re-fetch the order to show the existing request's details; fall back to a
  // generic notice if the lookup can't be repeated.
  async function handleAlreadyExists(payload) {
    try {
      const { res, data } = await fetchLookup(
        String(payload.order_number || '').replace(/^#/, ''),
        payload.customer_email || (state.order && state.order.email) || '');
      if (res.ok && data.existing_request) {
        showExistingRequest(data.existing_request);
        return;
      }
    } catch (err) { /* fall through to generic notice */ }
    showExistingRequest({ status: t('existing_generic') });
  }

  function showSuccess(refId) {
    $$('.returns-step').forEach((el) => el.classList.remove('is-active'));
    $('.returns-steps-nav').style.display = 'none';
    const ref = $('#returns-success-ref');
    if (refId && ref) ref.innerHTML = `${esc(t('success_ref_label'))} <span class="returns-ref">${esc(refId)}</span>`;

    const pickupBlock = $('#returns-success-pickup');
    if (pickupBlock) {
      if (pendingPickupPurchase && PICKUP_URL) {
        let url = PICKUP_URL;
        const params = ['recolha=1'];
        const enc = String((state.order && state.order.name) || '').replace(/^#/, '');
        if (enc) params.push('enc=' + encodeURIComponent(enc));
        if (refId) params.push('ref=' + encodeURIComponent(refId));
        // Number of items being picked up: selected return/exchange items.
        const qty = selectedIds().reduce((s, id) => s + (Number(state.selected[id].qty) || 0), 0);
        if (qty > 0) params.push('qty=' + qty);
        url += (url.indexOf('?') === -1 ? '?' : '&') + params.join('&');

        const btn = $('#returns-success-pickup-btn');
        if (btn) {
          btn.setAttribute('href', url);
          btn.onclick = function (ev) {
            ev.preventDefault();
            ev.stopPropagation();
            window.location.href = url;
          };
        }
        pickupBlock.style.display = 'flex';
      } else {
        pickupBlock.style.display = 'none';
      }
    }

    $('#returns-success').style.display = 'block';
    window.scrollTo({ top: root.offsetTop - 80, behavior: 'smooth' });
  }

  function setLoading(btn, loading) {
    if (!btn) return;
    btn.classList.toggle('is-loading', loading);
    btn.disabled = loading;
  }

  applyStaticI18n();
})();
